/**
 * ArBundleHeaderRow
 *
 * Renders the header row for a persistent bundle (group or kit).
 * Displays collapse caret, bundle type badge, bundle value, member count,
 * and per-bundle aggregation values for columns with aggregation configured.
 *
 * What each header cell means is decided by the shared
 * `resolveBundleHeaderCellValue`, and the bundle name by `resolveBundleLabel`,
 * so screen, PDF and XLSX agree (SYD-2276).
 * Invariant: a group's name is `bundleValue` (edited through the inline name
 * input), while a kit's name is its own first-column cell — kit headers
 * carry no `bundleValue`. The kit name goes through `ArCellRouter` on screen
 * and in export alike, so column-typed values print exactly the string the
 * screen shows.
 * Invariant: `memberRowIds` holds only the members this page paints, so `memberCount`
 * is a whole-page census the badge quotes while selection and "Add Member Below" stay
 * on the rendered ids (SYD-2406). The delete and ungroup copy quotes no count: both
 * act through a `{ bundleId }` scope the server expands over the whole collection, so
 * a page-spanning bundle loses more rows than this page shows. What each one did is
 * reported afterwards, from the counts the server returned (SYD-2430).
 * Invariant: `continuesOnNextPage` marks the earlier side of a pagination cut from
 * the header itself, so nothing has to be pinned to the last row of the page; the
 * later side is `ArBundleContinuationRow`. It carries no number — the badge counts
 * the members on this page, as it already does under a split (SYD-2430).
 * Invariant: a locked header is never draggable — its grip renders disabled —
 * since moving it would re-rank its members around a header that never moved.
 * Delete asks nothing before it runs (SYD-2447) — the callable's trash and the
 * undo buttons carry the way back — while Ungroup and Remove Annotation, which
 * have none, still confirm.
 * PDF group headers render the name full width (SYD-1992) with
 * the per-column aggregations on a second subgrid row beneath it, so long
 * names wrap without stealing the subtotals' columns. That row is captioned
 * "Subtotal" in column 0 and prints only when some column resolves to a
 * non-null aggregation, and the wrapper carries `break-inside: avoid` in PDF
 * so a page break cannot orphan the subtotals from their name (SYD-2278).
 * In card mode (below `md`) the header is a card on `colorBgLayout`: caret and
 * grip top left, actions top right, the name cell spanning both tracks as the
 * card title, and every later column as a label stacked above its value. The grip sets
 * `touch-action: none` in both modes so a finger drag reaches dnd-kit.
 */

import { CSSProperties, FC, Fragment, memo, useCallback, useContext, useId, useState } from 'react';
import { Button, Checkbox, Dropdown, Modal, Tag, Typography, theme } from 'antd';
import { usePdfExportMode } from '../usePdfExportMode';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  CloseOutlined,
  CommentOutlined,
  CopyOutlined,
  DeleteOutlined,
  DisconnectOutlined,
  HolderOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { proxy, useSnapshot } from 'valtio';
import { useStoreCollection, useStoreDocument } from '@/store';
import type { DocEntry } from '@/store';
import type {
  ArBundleType,
  ArCellValue,
  ArColumnDefinition,
  ArRowData,
  ArRowId,
  ArTableType,
  TypeId,
} from '#types/System.Artifacts.types';
import { AR_TABLE_AGGREGATIONS_ROW_ID } from '#types/System.Artifacts.types';
import { AR_TABLE_PAGE_SIZE } from '#types/System.Parameters.types';
import { resolveBundleHeaderCellValue, resolveBundleLabel } from '../bundleHeader';
import { ArTableActionsContext, ArTableUIStateContext } from '../context';
import { reportRowDeleteResult } from '../actions/reportRowDeleteResult';
import { deselectRow } from '../selection';
import { ContextMenu } from '@/providers/ContextMenu';
import { MessageContext } from '@/providers/MessageContext';
import { FieldTypeContext } from '@/providers/FieldTypeContext';
import resolveArColumnLabel from '../column/resolveArColumnLabel';
import ArCell from './ArCell';
import ArCellRouter from './ArCell/ArCellRouter';
import ArBundleNameInput from './ArBundleNameInput';
import ArRowAnnotation from './ArRowAnnotation';

const { Text } = Typography;

const EMPTY_HEADER_ENTRY = proxy<DocEntry<ArRowData>>({ data: {} as ArRowData });

const emptyProtectedSet: ReadonlySet<TypeId> = new Set<TypeId>();
const emptyFormulaSet: ReadonlySet<TypeId> = new Set<TypeId>();
const emptyFormulaSubFieldsByColumn: ReadonlyMap<TypeId, ReadonlySet<string>> = new Map();
const emptyKitCalcDriverSet: ReadonlySet<TypeId> = new Set<TypeId>();

type ArBundleHeaderRowProps = {
  rowId: ArRowId;
  bundleType: ArBundleType;
  memberRowIds: ArRowId[];
  memberCount: number;
  withheldMemberCount: number;
  collapsed: boolean;
  onToggleCollapsed: (bundleId: string) => void;
  /** The page-turn probe proved this bundle's block runs past the last row (SYD-2430). */
  continuesOnNextPage: boolean;
  columns: ArColumnDefinition[];
  disabled?: boolean;
  stickyFirstColumn?: boolean;
  frozenLeftCount?: number;
  frozenLeftOffsetsByIndex?: number[];
  manualReorderEnabled?: boolean;
  protectedTypeIds?: ReadonlySet<TypeId>;
  formulaTypeIds?: ReadonlySet<TypeId>;
  formulaSubFieldsByColumn?: ReadonlyMap<TypeId, ReadonlySet<string>>;
  kitCalcDriverTypeIds?: ReadonlySet<TypeId>;
  cardMode?: boolean;
};

const ArBundleHeaderRow: FC<ArBundleHeaderRowProps> = ({
  rowId,
  bundleType,
  memberRowIds,
  memberCount,
  withheldMemberCount,
  collapsed,
  onToggleCollapsed,
  continuesOnNextPage,
  columns,
  disabled = false,
  stickyFirstColumn = true,
  frozenLeftCount = 0,
  frozenLeftOffsetsByIndex = [],
  manualReorderEnabled = false,
  protectedTypeIds = emptyProtectedSet,
  formulaTypeIds = emptyFormulaSet,
  formulaSubFieldsByColumn = emptyFormulaSubFieldsByColumn,
  kitCalcDriverTypeIds = emptyKitCalcDriverSet,
  cardMode = false,
}) => {
  const { token } = theme.useToken();
  const { fieldTypes } = useContext(FieldTypeContext);
  const menuId = useId();
  const uiState = useContext(ArTableUIStateContext)!;
  const actions = useContext(ArTableActionsContext)!;
  const { messageApi } = useContext(MessageContext);
  const isPdfExportMode = usePdfExportMode();
  const [modalState, setModalState] = useState<'dissolve-bundle' | 'remove-annotation' | null>(null);
  const [addingMember, setAddingMember] = useState(false);
  const [duplicatingBundle, setDuplicatingBundle] = useState(false);
  const selectionSnap = useSnapshot(uiState.selection);
  const selectedRowsSnap = selectionSnap.selectedRows;

  /**
   * Slot presence is read off the snapshot before the bare proxy, so a slot that
   * arrives later re-renders this row. Both slots can be absent: the header's own
   * while its page is loading, and the aggregation row's on every rows-store
   * rebuild, since that document is outside the paginated query (SYD-2311).
   */
  const rowsStore = useStoreCollection<ArRowData>('rows');
  const rowsState = rowsStore.state;
  const rowsPersist = rowsStore.persist;
  const rowsSnap = rowsStore.useSnapshot(rowsState);
  const headerEntry = (rowsSnap.docs.has(rowId) && rowsState.docs.get(rowId)) || EMPTY_HEADER_ENTRY;
  const headerSnap = useSnapshot(headerEntry);
  const bundleValue = headerSnap.data?.bundleValue;

  /**
   * The header's own lock gates the grip, matching `ArRow`: `moveBundleInManualOrder`
   * delegates to `moveRowInManualOrder`, which returns early on a locked row, yet
   * still re-ranks every member into an interval derived from the position the
   * header never took — inverting it and laying the members above their own
   * header. A locked header is therefore not draggable at all (SYD-2406).
   */
  const isLocked = headerSnap.data?.locked === true;
  const canReorder = manualReorderEnabled && !disabled && !isLocked;
  /**
   * `disabled` is stated as an object because a header that cannot be picked up
   * must still receive dropped rows. `@dnd-kit/sortable`'s
   * `normalizeLocalDisabled` reads a bare boolean as
   * `{ draggable: value, droppable: false }` under a branch it labels "Backwards
   * compatibility", so the shape we want is unpinned until it is written out.
   */
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
    id: rowId,
    disabled: { draggable: !canReorder, droppable: false },
  });

  const schemaStore = useStoreDocument<ArTableType>('schema');
  const schemaSnap = schemaStore.useSnapshot(schemaStore.state);

  /**
   * `bundleAggregations` is written by the same server pass that refreshes the
   * table-wide totals, so the aggregation row's state is this header's progress
   * flag too — the header document itself never carries one. "Computing" means the
   * same thing here as in ArTableAggregations: an explicitly stale cache, or one
   * whose slot is not on screen yet. With aggregations off no pass ever runs, so
   * the absent slot is permanent and must not read as progress (SYD-2311).
   */
  const aggregationsEntry = (rowsSnap.docs.has(AR_TABLE_AGGREGATIONS_ROW_ID)
    && rowsState.docs.get(AR_TABLE_AGGREGATIONS_ROW_ID)) || EMPTY_HEADER_ENTRY;
  const aggregationsSnap = useSnapshot(aggregationsEntry);
  const isRecomputing = !isPdfExportMode
    && schemaSnap.data?.aggregationsEnabled === true
    && (aggregationsSnap.data?.upToDate === false || aggregationsSnap.data?.cells === undefined);
  const annotation = headerSnap.data?.annotation;
  const showAnnotation = annotation !== undefined;

  const firstColumnTypeId = columns[0]?.typeId;
  const firstColumnCellValue = (firstColumnTypeId
    ? headerSnap.data?.cells?.[firstColumnTypeId]?.value ?? null
    : null) as ArCellValue;

  const handleKitFirstColumnChange = useCallback((newValue: ArCellValue) => {
    if (!firstColumnTypeId) return;
    const mutableRow = rowsState.docs.get(rowId);
    if (!mutableRow?.data) return;
    if (!mutableRow.data.cells) mutableRow.data.cells = {};
    if (!mutableRow.data.cells[firstColumnTypeId]) mutableRow.data.cells[firstColumnTypeId] = { value: null };
    mutableRow.data.cells[firstColumnTypeId].value = newValue;
    rowsPersist.update(rowId, { [`cells.${firstColumnTypeId}.value`]: newValue }).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Valtio proxies (rowsState.docs) are stable refs
  }, [firstColumnTypeId, rowId, rowsPersist]);

  const allRowIds = [rowId, ...memberRowIds];

  const selectedInBundle = allRowIds.filter((id) => selectedRowsSnap[id] === true).length;

  const slotsUsedOutsideBundle = selectionSnap.selectedCount - selectedInBundle;
  const slotsAvailableForBundle = Math.max(0, AR_TABLE_PAGE_SIZE - slotsUsedOutsideBundle);
  const selectableInBundle = Math.min(allRowIds.length, slotsAvailableForBundle);
  const allSelected = selectableInBundle > 0 && selectedInBundle === selectableInBundle;
  const someSelected = selectedInBundle > 0 && selectedInBundle < selectableInBundle;

  const toggleBundleSelection = (): void => {
    const selection = uiState.selection;
    /** A hand tick cancels any escalation to the whole table, as a row tick does (SYD-2386). */
    selection.scope = 'visible';

    if (allSelected) {
      for (const id of allRowIds) {
        deselectRow(selection, id);
      }
      selection.lastClickedRowId = null;
      return;
    }

    for (const id of allRowIds) {
      if (selection.selectedRowIds.length >= AR_TABLE_PAGE_SIZE) break;
      if (selection.selectedRows[id] === true) continue;
      selection.selectedRows[id] = true;
      selection.selectedRowIndexById[id] = selection.selectedRowIds.length;
      selection.selectedRowIds.push(id);
      selection.selectedCount += 1;
    }
    selection.lastClickedRowId = null;
  };
  const bundleLabel = resolveBundleLabel({ bundleValue, bundleType, rowId });

  const bundleTypeLabel = bundleType === 'kit' ? 'Kit' : 'Group';

  const handleAddMemberBelow = async () => {
    setAddingMember(true);
    try {
      const lastMemberId = memberRowIds.length > 0 ? memberRowIds[memberRowIds.length - 1] : rowId;
      await actions.addMemberRow(rowId, lastMemberId);
    } finally {
      setAddingMember(false);
    }
  };

  /**
   * Additive and recoverable in one action ("Delete Group" on the copy), so it
   * fires without a confirmation at any size (SYD-2306). The copied bundle is the
   * true one: the server expands members from the whole collection, so its member
   * count can exceed the count this row's tag shows for a page-spanning bundle.
   */
  const handleDuplicateBundle = async () => {
    setDuplicatingBundle(true);
    try {
      const result = await actions.duplicateBundle(rowId);
      if (result.affected > 0) {
        const copiedMembers = result.affected - 1;
        messageApi.success(
          `Duplicated ${bundleTypeLabel.toLowerCase()} with ${copiedMembers} member row${copiedMembers === 1 ? '' : 's'}.`
        );
      }
    } finally {
      setDuplicatingBundle(false);
    }
  };

  /**
   * Fires without a confirmation (SYD-2447): the callable files the rows it destroys
   * in the trash and the undo buttons reverse the whole operation by id.
   *
   * Reports what the server touched, never what this page could see: the
   * `{ bundleId }` scope is expanded over the whole collection, so the response's
   * counts are the only honest ones (SYD-2430). The sentence comes from
   * `reportRowDeleteResult`, which every delete in the table shares; the bundle's
   * label is the one thing this path adds, because a delete refuses locked rows and
   * rows a Form Derive projection owns and keeps the header with them. The skip is
   * counted rather than attributed — the response never says which rows it was.
   * `failed` means the action already raised the reason.
   */
  const handleDeleteBundle = async () => {
    const result = await actions.deleteBundle(rowId);
    reportRowDeleteResult(messageApi, result, { bundle: bundleTypeLabel });
  };

  /**
   * Ungroup and Remove Annotation both keep their confirmation: neither is on the
   * undo stack, so the modal is the only thing standing between the click and the
   * write. Ungroup reports the server's count for the same reason a delete does.
   */
  const handleModalOk = async () => {
    const confirmed = modalState;
    setModalState(null);

    if (confirmed === 'remove-annotation') {
      await actions.updateAnnotation(rowId, undefined);
      return;
    }

    if (confirmed === 'dissolve-bundle') {
      const result = await actions.dissolveBundle(rowId);
      if (result.failed) return;
      messageApi.success(
        `Ungrouped ${result.affected} ${result.affected === 1 ? 'row' : 'rows'}.`
      );
    }
  };

  const contextMenuItems: MenuProps['items'] = [
    !showAnnotation && {
      key: 'add-annotation',
      label: 'Add Annotation',
      icon: <CommentOutlined />,
      onClick: () => actions.updateAnnotation(rowId, ''),
      disabled,
    },
    showAnnotation && {
      key: 'remove-annotation',
      label: 'Remove Annotation',
      icon: <CloseOutlined />,
      onClick: () => setModalState('remove-annotation'),
      disabled,
    },
    { type: 'divider' as const },
    {
      key: 'add-member',
      label: 'Add Member Below',
      icon: <PlusOutlined />,
      onClick: handleAddMemberBelow,
      disabled: disabled || addingMember,
    },
    {
      key: 'duplicate-bundle',
      label: `Duplicate ${bundleTypeLabel}`,
      icon: <CopyOutlined />,
      onClick: handleDuplicateBundle,
      disabled: disabled || duplicatingBundle,
    },
    { type: 'divider' as const },
    {
      key: 'dissolve-bundle',
      label: `Ungroup ${bundleTypeLabel}`,
      icon: <DisconnectOutlined />,
      onClick: () => setModalState('dissolve-bundle'),
      disabled,
    },
    {
      key: 'delete-bundle',
      label: `Delete ${bundleTypeLabel}`,
      icon: <DeleteOutlined />,
      onClick: () => { void handleDeleteBundle(); },
      disabled,
      danger: true,
    },
  ].filter(Boolean) as MenuProps['items'];

  const pdfGroupHeaderResolutions =
    isPdfExportMode && bundleType === 'group'
      ? columns.map((column, idx) =>
          resolveBundleHeaderCellValue({
            rowData: headerSnap.data,
            column,
            bundleType,
            kitCalcDriverTypeIds,
            isFirstColumn: idx === 0,
          }),
        )
      : null;
  const showPdfGroupAggregationRow =
    pdfGroupHeaderResolutions?.some((r) => r.kind === 'aggregation' && r.value !== null) ?? false;

  const checkboxCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: token.marginXXS,
    backgroundColor: token.colorBgLayout,
    borderRight: `1px solid ${token.colorBorderSecondary}`,
    borderBottom: showAnnotation ? 'none' : `1px solid ${token.colorBorderSecondary}`,
    borderTop: `1px dashed ${token.colorBorder}`,
    boxSizing: 'border-box',
    ...(cardMode
      ? {
          gridRow: 1,
          gridColumnStart: 1,
          justifyContent: 'flex-start',
          borderRight: 'none',
          borderBottom: 'none',
          borderTop: 'none',
        }
      : null),
  };

  if (!isPdfExportMode && stickyFirstColumn) {
    checkboxCellStyle.position = 'sticky';
    checkboxCellStyle.left = 0;
    checkboxCellStyle.zIndex = 20;
  }

  const dataCellStyleBase: CSSProperties = {
    position: 'relative',
    height: isPdfExportMode ? 'auto' : token.controlHeight,
    maxHeight: isPdfExportMode ? undefined : token.controlHeight,
    minHeight: token.controlHeight,
    display: 'flex',
    alignItems: isPdfExportMode ? 'flex-start' : 'center',
    borderRight: `2px solid ${token.colorBorderSecondary}60`,
    borderBottom:
      showAnnotation || showPdfGroupAggregationRow ? 'none' : `1px solid ${token.colorBorderSecondary}`,
    borderRadius: 0,
    backgroundColor: token.colorBgContainer,
    boxSizing: 'border-box',
    minWidth: 0,
    overflow: isPdfExportMode ? 'visible' : 'hidden',
    overflowWrap: isPdfExportMode ? 'anywhere' : undefined,
    wordBreak: isPdfExportMode ? 'break-word' : undefined,
    whiteSpace: isPdfExportMode ? 'normal' : undefined,
    width: '100%',
    flex: 1,
    padding: `${token.paddingXXS}px ${token.paddingXS}px`,
    ...(cardMode ? { borderRight: 'none', borderBottom: 'none' } : null),
  };

  const actionCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderLeft: `2px solid ${token.colorBorderSecondary}60`,
    borderBottom: showAnnotation ? 'none' : `1px solid ${token.colorBorderSecondary}`,
    borderTop: `1px dashed ${token.colorBorder}`,
    backgroundColor: token.colorBgLayout,
    boxSizing: 'border-box',
    position: isPdfExportMode ? 'static' : 'sticky',
    ...(isPdfExportMode ? null : { right: 0, zIndex: 20 }),
    ...(cardMode
      ? {
          gridRow: 1,
          gridColumnStart: 2,
          justifySelf: 'end',
          position: 'static',
          borderLeft: 'none',
          borderBottom: 'none',
          borderTop: 'none',
        }
      : null),
  };

  return (
    <ContextMenu menuId={menuId} items={contextMenuItems} disabled={disabled}>
    <div
      ref={setNodeRef}
      style={{
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        gridColumn: '1 / -1',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        boxShadow: isDragging ? token.boxShadowSecondary : undefined,
        breakInside: isPdfExportMode ? 'avoid' : undefined,
        pageBreakInside: isPdfExportMode ? 'avoid' : undefined,
        ...(cardMode
          ? {
              backgroundColor: token.colorBgLayout,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: token.paddingXS,
              rowGap: token.paddingXXS,
              overflow: 'hidden',
            }
          : null),
      }}
    >
    <div
      aria-label={`artable-bundle-header-${rowId}`}
      role="row"
      style={{
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        gridColumn: '1 / -1',
      }}
    >
      {!isPdfExportMode && (
        <div role="cell" style={checkboxCellStyle}>
          <Button
            aria-label={`artable-toggle-bundle-collapse-${rowId}`}
            type="text"
            size="small"
            disabled={disabled}
            icon={collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapsed(rowId);
            }}
            style={{ padding: 0 }}
          />
          {manualReorderEnabled && !disabled && (
            <Button
              {...attributes}
              {...listeners}
              type="text"
              size="small"
              disabled={isLocked}
              aria-label={`artable-bundle-drag-handle-${rowId}`}
              icon={<HolderOutlined style={{ fontSize: token.fontSizeSM }} />}
              style={{
                ...(cardMode
                  ? {
                      width: token.controlHeight,
                      minWidth: token.controlHeight,
                      height: token.controlHeight,
                    }
                  : null),
                padding: 0,
                touchAction: 'none',
                cursor: isLocked ? 'not-allowed' : 'grab',
                color: isLocked ? token.colorTextDisabled : token.colorTextSecondary,
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          )}
        </div>
      )}

      {columns.map((column, idx) => {
        if (isPdfExportMode && bundleType === 'group' && idx > 0) return null;

        const resolution = resolveBundleHeaderCellValue({
          rowData: headerSnap.data,
          column,
          bundleType,
          kitCalcDriverTypeIds,
          isFirstColumn: idx === 0,
        });

        const style: CSSProperties = {
          ...dataCellStyleBase,
          backgroundColor: token.colorBgLayout,
          borderTop: cardMode ? 'none' : `1px dashed ${token.colorBorder}`,
          ...(cardMode ? { gridColumnStart: 1, gridColumnEnd: -1 } : null),
        };
        const cardLabel = cardMode && idx > 0 ? (
          <Text
            type="secondary"
            style={{ gridColumnStart: 1, gridColumnEnd: -1 }}
          >
            {resolveArColumnLabel(column, fieldTypes)}
          </Text>
        ) : null;

        if (!isPdfExportMode && stickyFirstColumn && idx < frozenLeftCount) {
          const left = frozenLeftOffsetsByIndex[idx];
          if (left !== undefined) {
            style.position = 'sticky';
            style.left = left;
            style.zIndex = 20 + (frozenLeftCount - idx);
            style.backdropFilter = 'blur(3px)';
            style.backgroundColor = `color-mix(in srgb, ${token.colorBgLayout} 56%, transparent)`;
          }
        }

        if (isPdfExportMode && bundleType === 'group' && idx === 0) {
          style.gridColumn = '1 / -1';
          style.borderRight = 'none';
        }

        if (resolution.kind === 'bundleValue' || resolution.kind === 'ownCell') {
          return (
            <Fragment key={`bundle-${rowId}-cell-${column.typeId}`}>
            {cardLabel}
            <div
              aria-label={`artable-bundle-header-cell-${rowId}-${column.typeId}`}
              role="cell"
              style={style}
            >
              <div style={{ 
                display: 'flex', 
                flexGrow: 1,
                alignItems: 'center', 
                gap: token.paddingXS, 
                minWidth: 0 
              }}>
                {!isPdfExportMode && (
                  <Checkbox
                    aria-label={`artable-select-bundle-${rowId}`}
                    disabled={disabled}
                    checked={allSelected}
                    indeterminate={someSelected}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBundleSelection();
                    }}
                  />
                )}
                {resolution.kind === 'ownCell' ? (
                  <ArCellRouter
                    column={column}
                    value={firstColumnCellValue}
                    onChange={handleKitFirstColumnChange}
                    disabled={disabled || isPdfExportMode}
                    cellId={`artable-bundle-cell-${column.typeId}-${rowId}`}
                    rowId={rowId}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                ) : isPdfExportMode ? (
                  <Text strong style={{ minWidth: 0 }}>
                    {bundleLabel}
                  </Text>
                ) : (
                  <ArBundleNameInput
                    key={bundleLabel}
                    rowId={rowId}
                    bundleLabel={bundleLabel}
                    disabled={disabled}
                  />
                )}
                {continuesOnNextPage && !isPdfExportMode && (
                  <Text
                    aria-label={`artable-bundle-continues-on-next-page-${rowId}`}
                    type="secondary"
                    style={{ fontSize: token.fontSizeSM }}
                  >
                    Continues on next page
                  </Text>
                )}
                <Tag
                  aria-label={`artable-bundle-member-count-${rowId}`}
                  style={{
                    marginInlineEnd: 0,
                    marginLeft: 'auto',
                  }}
                >
                  {bundleTypeLabel} · {withheldMemberCount > 0
                    ? `${memberRowIds.length} of ${memberCount}`
                    : memberCount}
                </Tag>
              </div>
            </div>
            </Fragment>
          );
        }

        if (resolution.kind === 'driver') {
          return (
            <Fragment key={`bundle-${rowId}-cell-${column.typeId}`}>
            {cardLabel}
            <ArCell
              column={column}
              rowId={rowId}
              disabled={disabled}
              columnIndex={idx}
              stickyFirstColumn={stickyFirstColumn}
              frozenLeftCount={frozenLeftCount}
              frozenLeftOffsetsByIndex={frozenLeftOffsetsByIndex}
              isOddRow={false}
              isRowLocked={false}
              isColumnProtected={protectedTypeIds.has(column.typeId)}
              isFormulaColumn={formulaTypeIds.has(column.typeId)}
              formulaSubFields={formulaSubFieldsByColumn.get(column.typeId)}
              cardMode={cardMode}
            />
            </Fragment>
          );
        }

        return (
          <Fragment key={`bundle-${rowId}-cell-${column.typeId}`}>
          {cardLabel}
          <div
            aria-label={`artable-bundle-header-cell-${rowId}-${column.typeId}`}
            role="cell"
            style={style}
          >
            {resolution.kind === 'aggregation' && resolution.value !== null && (
              <Text
                type="secondary"
                className={isRecomputing ? 'calculating-value' : undefined}
                style={{ fontSize: token.fontSizeSM }}
              >
                {typeof resolution.value === 'number'
                  ? resolution.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                  : String(resolution.value)}
              </Text>
            )}
            {resolution.kind === 'aggregation' && resolution.value === null && isRecomputing && (
              <span
                aria-label={`artable-bundle-header-computing-${rowId}-${column.typeId}`}
                className="calculating-value"
              >
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>—</Text>
              </span>
            )}
          </div>
          </Fragment>
        );
      })}

      {!isPdfExportMode && (
        <div aria-label={`artable-bundle-header-actions-${rowId}`} role="cell" style={actionCellStyle}>
          <Dropdown menu={{ items: contextMenuItems }} trigger={['click']} disabled={disabled}>
            <Button
              type="text"
              icon={<MoreOutlined />}
              aria-label="Row actions"
              size="small"
              style={{ padding: 4 }}
            />
          </Dropdown>
        </div>
      )}
    </div>
    {showPdfGroupAggregationRow && pdfGroupHeaderResolutions && (
      <div
        aria-label={`artable-bundle-header-aggregation-row-${rowId}`}
        role="row"
        style={{
          display: 'grid',
          gridTemplateColumns: 'subgrid',
          gridColumn: '1 / -1',
        }}
      >
        {columns.map((column, idx) => {
          const resolution = pdfGroupHeaderResolutions[idx];
          return (
            <div
              key={`bundle-${rowId}-aggregation-${column.typeId}`}
              aria-label={`artable-bundle-header-aggregation-${rowId}-${column.typeId}`}
              role="cell"
              style={{
                ...dataCellStyleBase,
                backgroundColor: token.colorBgLayout,
                borderBottom: showAnnotation ? 'none' : `1px solid ${token.colorBorderSecondary}`,
                ...(idx === columns.length - 1 ? { borderRight: 'none' } : null),
              }}
            >
              {idx === 0 && (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  Subtotal
                </Text>
              )}
              {resolution.kind === 'aggregation' && resolution.value !== null && (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {typeof resolution.value === 'number'
                    ? resolution.value.toLocaleString(undefined, { maximumFractionDigits: 2 })
                    : String(resolution.value)}
                </Text>
              )}
            </div>
          );
        })}
      </div>
    )}
    {showAnnotation && (
      <ArRowAnnotation
        rowId={rowId}
        annotation={annotation}
        isLocked={false}
        disabled={disabled}
        rowBackground={token.colorBgLayout}
      />
    )}
    </div>
    <Modal
      title={
        modalState === 'remove-annotation'
          ? 'Remove Annotation'
          : `Ungroup ${bundleTypeLabel}`
      }
      open={modalState !== null}
      onOk={handleModalOk}
      onCancel={() => setModalState(null)}
      okText={modalState === 'remove-annotation' ? 'Remove' : 'Ungroup'}
      cancelText="Cancel"
      aria-label={
        modalState === 'remove-annotation'
          ? 'Remove annotation confirmation'
          : 'Dissolve bundle confirmation'
      }
    >
      {modalState === 'dissolve-bundle' && (
        <Text>
          Are you sure you want to ungroup this {bundleTypeLabel.toLowerCase()}? All its member rows will become independent rows.
        </Text>
      )}
      {modalState === 'remove-annotation' && (
        <Text>
          Are you sure you want to remove the annotation from this {bundleTypeLabel.toLowerCase()}?
        </Text>
      )}
    </Modal>
    </ContextMenu>
  );
};

export default memo(ArBundleHeaderRow);
